import React, { useMemo, memo } from "react";
import {
  Card, CardActionArea, Box, Typography, Stack, Chip, IconButton, useMediaQuery
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import Iconify from "./Iconify";

// ---- Utils
const toDateSafe = (d) => (!d ? null : (typeof d?.toDate === "function" ? d.toDate() : new Date(d)));

const formatDateRange = (start, end) => {
  const s = toDateSafe(start);
  const e = toDateSafe(end);
  if (!s && !e) return "";
  if (s && !e) return format(s, "dd MMM yyyy • HH:mm", { locale: fr });
  if (!s && e) return format(e, "dd MMM yyyy • HH:mm", { locale: fr });

  const sameDay = s.getFullYear() === e.getFullYear()
    && s.getMonth() === e.getMonth()
    && s.getDate() === e.getDate();

  if (sameDay) {
    return `${format(s, "dd MMM yyyy", { locale: fr })} • ${format(s, "HH:mm", { locale: fr })}–${format(e, "HH:mm", { locale: fr })}`;
  }
  if (s.getFullYear() === e.getFullYear()) {
    return `${format(s, "dd MMM • HH:mm", { locale: fr })} — ${format(e, "dd MMM • HH:mm", { locale: fr })}`;
  }
  return `${format(s, "dd MMM yyyy • HH:mm", { locale: fr })} — ${format(e, "dd MMM yyyy • HH:mm", { locale: fr })}`;
};

const formatPrice = (minPrice, currency = "XOF") => {
  if (minPrice === 0 || String(minPrice).toLowerCase() === "gratuit") return "Gratuit";
  if (minPrice == null || minPrice === "") return "Tarifs variables";
  try {
    const nf = new Intl.NumberFormat("fr-FR");
    return `À partir de ${nf.format(Number(minPrice))} ${currency}`;
  } catch {
    return `À partir de ${minPrice} ${currency}`;
  }
};

function DateBadge({ date }) {
  const d = toDateSafe(date);
  if (!d) return null;
  return (
    <Stack
      sx={{
        position: "absolute", top: 12, left: 12,
        color: "white", backdropFilter: "blur(5px)", // effet flou type iOS
                backgroundColor: "rgba(255, 255, 255, 0.5)",
         borderRadius: 1.5, px: 1, py: 0.5,
        alignItems: "center",
      }}
      spacing={0}
    >
      <Typography fontSize={11} fontWeight={500} sx={{ textTransform: "uppercase", lineHeight: 1 }}>
        {format(d, "MMM", { locale: fr })}
      </Typography>
      <Typography fontSize={16} fontWeight={800} lineHeight={1.1}>
        {format(d, "dd", { locale: fr })}
      </Typography>
    </Stack>
  );
}

const EventCard = ({
  img,
  currency = "XOF", // XOF pour CFA
  name,
  eventID,
  debut,
  fin,
  city,
  lieu,
  type,
  ev,               // snapshot ou plain object
  organisateur,
  online = false,
  soldOut = false,
}) => {
  const evData = useMemo(() => (ev && typeof ev.data === "function" ? ev.data() : ev) ?? {}, [ev]);
  const isMultiDates = !!evData?.seanceActive
    || (debut && fin && toDateSafe(debut)?.toDateString() !== toDateSafe(fin)?.toDateString());

  const dateRangeText = useMemo(() => formatDateRange(debut, fin), [debut, fin]);
  const priceLabel = useMemo(() => formatPrice(evData?.minPrice, currency), [evData?.minPrice, currency]);
  const isMobile = useMediaQuery("(max-width:900px)");

  const onShare = async (e) => {
    e.preventDefault(); // évite navigation
    const url = `${window.location.origin}/fr/event/${eventID}`;
    const title = name ?? "Événement";
    const text = `${title}${city ? " • " + city : ""}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
      } else {
        await navigator.clipboard.writeText(url);
      }
    } catch { /* no-op */ }
  };

  const onLike = (e) => {
    e.preventDefault();
    // TODO: appeler ta logique (Firestore / favorites) + toast
  };

  const onImgError = (ev) => {
    ev.currentTarget.src =
      "https://cdn.jsdelivr.net/gh/placeholderlogo/img@main/placeholder-1200x675.png";
  };

  return (
    <Card
      elevation={0}
      sx={{
        mx: 1,
        width:'100%',
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: "0 6px 24px rgba(0,0,0,0.08)",
        bgcolor: "#fff",
        transition: "box-shadow .25s ease, transform .2s ease",
        "&:hover": { boxShadow: "0 10px 30px rgba(0,0,0,0.12)", transform: { md: "translateY(-2px)" } },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/fr/event/${eventID}`}
        aria-label={`Ouvrir l’événement ${name}`}
        sx={{ alignItems: "stretch" }}
      >
        {/* Cover */}
        <Box sx={{ position: "relative" }}>
          <Box
            component="img"
            src={img}
            alt={name}
            onError={onImgError}
            loading="lazy"
            decoding="async"
            srcSet={`${img} 1x, ${img} 2x`}
            sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 360px"
            sx={{
              aspectRatio: "16 / 9",
              objectFit: "cover",
              width: "100%",
              display: "block",
              bgcolor: "#f2f2f2",
              transition: "opacity .3s ease",
            }}
          />

          {/* Gradient bas */}
          <Box
            sx={{
              position: "absolute", insetInline: 0, bottom: 0, height: "35%",
              background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%)",
            }}
          />
          <DateBadge date={debut} />

          {/* Badges conditionnels à droite */}
          <Stack direction="row" spacing={1} sx={{ position: "absolute", top: 12, right: 12 }}>
            {isMultiDates && (
              <Chip label="Plusieurs dates" size="small" sx={{color: "white", backdropFilter: "blur(5px)", // effet flou type iOS
                backgroundColor: "rgba(255, 255, 255, 0.5)", fontWeight: 500 }} />
            )}
            {(online || evData?.locationType === 1) && (
              <Chip label="En ligne" size="small" sx={{ color: "white", backdropFilter: "blur(5px)", // effet flou type iOS
                backgroundColor: "rgba(255, 255, 255, 0.5)", fontWeight: 600 }} />
            )}
            {soldOut && (
              <Chip label="Épuisé" color="error" size="small" sx={{ fontWeight: 700 }} />
            )}
          </Stack>

          {/* Actions like/share : hover desktop, visibles mobile */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              position: "absolute",
              left: 12,
              bottom: 12,
              opacity: isMobile ? 1 : 0,
              transform: isMobile ? "translateY(0)" : "translateY(8px)",
              transition: "opacity .2s ease, transform .2s ease",
              ".MuiCardActionArea-root:hover &": { opacity: 1, transform: "translateY(0)" },
            }}
          >
            <IconButton
              size="small"
              onClick={onLike}
              aria-label="Ajouter aux favoris"
              sx={{ bgcolor: "rgba(255,255,255,0.95)", "&:hover": { bgcolor: "#fff" } }}
            >
              <Iconify icon="mdi:cards-heart-outline" width={18} height={18}  />
            </IconButton>
            <IconButton
              size="small"
              onClick={onShare}
              aria-label="Partager l’événement"
              sx={{ bgcolor: "rgba(255,255,255,0.95)", "&:hover": { bgcolor: "#fff" } }}
            >
              <Iconify icon="ph:share-network-fill" width={18} height={18} />
            </IconButton>
          </Stack>
        </Box>

        {/* Contenu */}
        <Box sx={{ p: 2 }}>
          <Typography component="h3" fontSize={19} fontWeight={500} sx={{ mb: 0.5, lineHeight: 1.2 }}>
            {name}
          </Typography>

          {!!organisateur && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Organisé par</Typography>
              <Typography variant="body2" fontWeight={500} color="text.secondary">{organisateur}</Typography>
            </Stack>
          )}

          {!!dateRangeText && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Iconify icon="carbon:calendar" width={20} height={20} style={{ color: "#5E17EB" }} />
              <Typography variant="body2" sx={{ color: "#5E17EB", fontWeight: 600 }}>
                {dateRangeText}
              </Typography>
            </Stack>
          )}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Iconify icon="carbon:location" width={20} height={20} />
            <Typography variant="body2" color="text.secondary">
              {online || evData?.locationType === 1 ? "Événement en ligne" : [lieu, city].filter(Boolean).join(", ")}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" fontWeight={500}>{priceLabel}</Typography>
            {type && (
              <Chip
                label={type}
                size="small"
                variant="outlined"
                sx={{ borderColor: "#eee", color: "text.secondary" }}
              />
            )}
          </Stack>
        </Box>
      </CardActionArea>
    </Card>
  );
};

export default memo(EventCard);
